import { type PropsWithChildren, useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Card, CardContent, ListItemButton, styled, Switch, Typography } from '@mui/material';

import { Collapsible } from './index';
import { IconTooltip } from '../Tooltip/IconTooltip';

export const CardStyled = styled(Card)`
    margin-top: 12px;
    max-width: 500px;
` as typeof Card;

export const CardContentStyled = styled(CardContent)`
    padding: 8px 12px;

    &:last-child {
        padding-bottom: 8px;
    }
` as typeof CardContent;

const ExpandMoreButton = styled(ListItemButton)`
    display: flex;
    justify-content: flex-start;
    font-size: 16px;

    padding: 0;
    border: 0;
    outline: 0;

    svg:first-of-type {
        margin-right: 8px;
    }

    &:hover {
        background-color: transparent;
    }
` as typeof ListItemButton;

export const CardTitle = styled(Typography)`
    color: ${({ theme }) => theme.palette.text.primary};
    width: 100%;
` as typeof Typography;

const CollapsedContent = styled(Box)`
    margin-top: 14px;

    &:last-child {
        padding-bottom: 0;
    }
` as typeof Box;

const CardRow = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    padding-bottom: 8px;
` as typeof Box;

const TitleRow = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: row;
    align-items: center;
` as typeof Box;

export type CollapsibleCardRow = {
    key: string;
    value?: string;
    element?: JSX.Element;
};

export type CollapsibleCardToggle = {
    name: string;
    checkedState: any;
    onChange: (event: any) => void;
    disabled?: boolean;
    hint?: string;
};

export type CollapsibleCardProps = PropsWithChildren<{
    title: string;
    titleIcon?: JSX.Element;
    id: string;
    rows: CollapsibleCardRow[];
    toggle?: CollapsibleCardToggle;
    defaultExpanded?: boolean;
}>;

export const CollapsibleCard = (props: CollapsibleCardProps) => {
    const { id, title, titleIcon, toggle, rows, defaultExpanded, children } = props;

    const [expanded, setExpanded] = useState(toggle?.checkedState || defaultExpanded);

    const onClickExpandCard = (event?: any) => {
        setExpanded((previous) => !previous);
        toggle?.onChange(event);
    };

    return (
        <CardStyled variant="outlined">
            <CardContentStyled>
                {toggle ? (
                    <TitleRow>
                        <Switch
                            name={toggle.name}
                            disabled={toggle.disabled}
                            checked={toggle.checkedState}
                            onChange={toggle.onChange}
                        />
                        <CardTitle>{title}</CardTitle>
                        {toggle.hint && <IconTooltip>{toggle.hint}</IconTooltip>}
                    </TitleRow>
                ) : (
                    <ExpandMoreButton aria-controls={id} onClick={onClickExpandCard}>
                        {titleIcon}
                        <CardTitle>{title}</CardTitle>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </ExpandMoreButton>
                )}

                {rows.length > 0 && (
                    <Collapsible id={id} expanded={toggle ? toggle.checkedState : expanded}>
                        <CollapsedContent>
                            {rows.map((row) => (
                                <CardRow key={row.key}>
                                    {row.element
                                        ? row.element
                                        : [
                                              <Typography key="key" color="text.primary">
                                                  {row.key}
                                              </Typography>,
                                              <Typography key="value" color="text.secondary">
                                                  {row.value}
                                              </Typography>
                                          ]}
                                </CardRow>
                            ))}

                            {children}
                        </CollapsedContent>
                    </Collapsible>
                )}
            </CardContentStyled>
        </CardStyled>
    );
};
